import { useEffect, useMemo, useState } from "react";
import { AU_KM, REAL_UNITS_PER_AU, type BodyId } from "@solar/ephemeris";
import { frame } from "../scene/frame";
import { useScene, type HideId, type Layers, type Quality } from "../store";
import { bodyName, useT } from "../i18n";

const EYE_ON = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></svg>;
const EYE_OFF = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17.9 17.9A10.4 10.4 0 0 1 12 19C5.6 19 2 12 2 12a18 18 0 0 1 4.1-5.1M9.9 5.2A9.7 9.7 0 0 1 12 5c6.4 0 10 7 10 7a18 18 0 0 1-2.2 3.2M1 1l22 22M14.1 14.1a3 3 0 0 1-4.2-4.2" /></svg>;

const PLANETS: BodyId[] = ["mercury", "venus", "earth", "mars", "jupiter", "saturn", "uranus", "neptune"];
const DWARFS: BodyId[] = ["pluto", "ceres", "haumea", "makemake", "eris"];
const SMALL: HideId[] = ["belt", "kuiper", "halley"];
const DOT: Record<string, string> = { belt: "#c9bda9", kuiper: "#8fb0d8", halley: "#bfeaff", sun: "#ffb454", mercury: "#cfc6bb", venus: "#f1dca5", earth: "#3f7fd8", moon: "#bbbbbb", mars: "#e08a5c", jupiter: "#e0c39c", saturn: "#eddba4", uranus: "#b9eef0", neptune: "#6f95f5", pluto: "#e4cfb8", ceres: "#a79f97", haumea: "#e8e8ee", makemake: "#d8a58a", eris: "#ece6df" };

export function Sidebar() {
  const { t, lang } = useT();
  const { hidden, selected, select, setHidden, layers, toggleLayer, mode, setMode } = useScene();
  const groupState = (ids: HideId[]) => { const n = ids.filter((i) => !hidden.has(i)).length; return n === ids.length ? "on" : n === 0 ? "off" : "mix"; };
  const planetsWithMoon = useMemo(() => [...PLANETS, "moon" as BodyId], []);

  const Row = ({ id, sub }: { id: HideId; sub?: boolean }) => {
    const off = hidden.has(id) || (id === "moon" && hidden.has("earth"));
    const particles = id === "belt" || id === "kuiper";
    return (
      <div className={`row ${sub ? "sub" : ""} ${!particles && selected === id ? "sel" : ""} ${off ? "off" : ""}`} onClick={() => !off && !particles && select(id as BodyId)} title={particles ? t("beltNote") : undefined} style={particles ? { cursor: "default" } : undefined}>
        <span className="dot" style={{ background: DOT[id] }} />
        <span className="nm">{particles ? t(id) : bodyName(id as BodyId, lang)}</span>
        <button className={`eye ${hidden.has(id) ? "off" : ""}`} title={hidden.has(id) ? t("show") : t("hide")} onClick={(e) => { e.stopPropagation(); setHidden([id], !hidden.has(id)); }}>
          {hidden.has(id) ? EYE_OFF : EYE_ON}
        </button>
      </div>
    );
  };
  const Group = ({ title, ids }: { title: string; ids: HideId[] }) => {
    const st = groupState(ids);
    return (
      <div className="grp">
        <h4>{title}</h4>
        <button className={`eye ${st === "on" ? "" : st}`} title={st === "on" ? t("hideAll") : t("showAll")} onClick={() => setHidden(ids, st === "on")}>{st === "off" ? EYE_OFF : EYE_ON}</button>
      </div>
    );
  };
  const chip = (k: keyof Layers, label: string) => <span key={k} className={`chip ${layers[k] ? "on" : ""}`} onClick={() => toggleLayer(k)}>{label}</span>;

  return (
    <aside className="side glass">
      <div className="brand"><i /><div><b>{t("appName")}</b><span>{t("appSub")}</span></div></div>
      <div>
        <div className={`seg ${mode === "real" ? "real" : ""}`}>
          <div className="thumb" />
          <button className={mode === "model" ? "on" : ""} onClick={() => setMode("model")}>{t("model")}</button>
          <button className={mode === "real" ? "on" : ""} onClick={() => setMode("real")}>{t("real")}</button>
        </div>
        <p className="hint" style={{ marginTop: 8 }}>{mode === "model" ? t("hintModel") : t("hintReal")}</p>
      </div>
      <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
        <h4>{t("bodies")}</h4>
        <div className="list">
          <Row id="sun" />
          <Group title={t("planets")} ids={planetsWithMoon} />
          {PLANETS.map((id) => (<div key={id}><Row id={id} />{id === "earth" && <Row id="moon" sub />}</div>))}
          <Group title={t("dwarfs")} ids={DWARFS} />
          {DWARFS.map((id) => <Row key={id} id={id} />)}
          <Group title={t("smallBodies")} ids={SMALL} />
          {SMALL.map((id) => <Row key={id} id={id} />)}
        </div>
      </div>
      <div>
        <h4>{t("layers")}</h4>
        <div className="tg">
          {chip("orbit", t("orbit"))}{chip("trail", t("trail"))}{chip("axis", t("axis"))}{chip("label", t("labels"))}{chip("moonOrbit", t("moonOrbit"))}{chip("auto", t("auto"))}
        </div>
      </div>
      <Quality />
      <Ruler />
    </aside>
  );
}

/** 比例尺：真实比例下按当前缩放自动换单位（AU → 万公里 → km）；模型模式只提示“已压缩”。 */
function Ruler() {
  const { t } = useT();
  const [st, setSt] = useState({ px: 1, m: 0 });
  useEffect(() => { const id = setInterval(() => setSt({ px: frame.pxPerUnit, m: frame.m }), 150); return () => clearInterval(id); }, []);
  if (st.m < 0.5) return <div className="scale"><div className="bar" style={{ width: 70 }} /><span className="num">{t("scaleModel")}</span></div>;
  const pxPerAU = st.px * REAL_UNITS_PER_AU;
  const nice = (x: number) => { const e = Math.pow(10, Math.floor(Math.log10(x))), m = x / e; return (m < 1.5 ? 1 : m < 3.5 ? 2 : m < 7.5 ? 5 : 10) * e; };
  const au = nice(90 / pxPerAU);
  let label: string, px: number;
  if (au >= 0.01) { label = `${au >= 1 ? au : au} AU`; px = au * pxPerAU; }
  else { const km = nice(au * AU_KM); label = km >= 1e4 ? `${km / 1e4} ${t("wanKm")}` : `${km.toLocaleString()} km`; px = (km / AU_KM) * pxPerAU; }
  return <div className="scale"><div className="bar" style={{ width: Math.max(16, Math.min(200, px)) }} /><span className="num">{label} · {t("trueScale")}</span></div>;
}

/** 画质分档：自动按设备判断；也可手动指定（影响像素比、网格精度、粒子数量）。 */
function Quality() {
  const { t } = useT();
  const { quality, setQuality, setCreditsOpen } = useScene();
  const opts: [Quality, "qAuto" | "qLow" | "qMedium" | "qHigh"][] = [["auto", "qAuto"], ["low", "qLow"], ["medium", "qMedium"], ["high", "qHigh"]];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <h4 style={{ margin: 0, marginRight: 4 }}>{t("quality")}</h4>
      {opts.map(([k, label]) => (<span key={k} className={`chip ${quality === k ? "on" : ""}`} onClick={() => setQuality(k)}>{t(label)}</span>))}
      <span className="chip" style={{ marginLeft: "auto" }} onClick={() => setCreditsOpen(true)}>{t("credits")}</span>
    </div>
  );
}
